// =============================================================================================
// ==== Utworzony w Krok 4A >>>>

import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchNodeHierarchy } from '../../../store/slices/nodesSlice';

interface NodeValue {
  id: number;
  key: string;
  name: string;
}

interface NodeTreePickerProps {
  rootNodeId: number;
  onSelect: (node: NodeValue) => void;
}

export const NodeTreePicker: React.FC<NodeTreePickerProps> = ({
  rootNodeId,
  onSelect
}) => {
  const dispatch = useAppDispatch();
  
  
  
  //const { nodes, loading, error } = useAppSelector((state: any) => state.nodes);
  const { hierarchy, loading, error } = useAppSelector((state: any) => state.nodes);
  


  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);


// ==== Temp DEBUG - DODAJ TUTAJ:
  console.log('NodeTreePicker debug:', { 
  nodes: hierarchy,  // zmienione z nodes na hierarchy
  nodesLength: hierarchy?.length || 0,
  loading, 
  error,
  rootNodeId 
});



  // Load hierarchy on mount
  useEffect(() => {
    
    dispatch(fetchNodeHierarchy({ includeChildren: true, maxDepth: 5 }));
    //dispatch(fetchNodeHierarchy({ parentNodeId: rootNodeId, includeChildren: true, maxDepth: 5 }));
    //dispatch(fetchNodeHierarchy({ parentNodeId: rootNodeId, maxDepth: 5 }));

  }, [dispatch, rootNodeId]);

  
  
  
  // Handle node selection
  // const handleNodeClick = (nodeId: string) => {
  //   const node = findNodeById(nodes, parseInt(nodeId));
  //   if (node && node.nodeId !== rootNodeId) { // Don't allow selecting root node
  //     onSelect({
  //       id: node.nodeId,
  //       key: node.key || `node_${node.nodeId}`, // Fallback if key not available
  //       name: node.name
  //     });
  //   }
  // };

const handleNodeClick = (nodeId: string) => {
    const node = findNodeById(hierarchy, parseInt(nodeId));
    if (node && node.nodeId !== rootNodeId) { // Don't allow selecting root node
      onSelect({
        id: node.nodeId,
        key: node.key || `node_${node.nodeId}`, // Fallback if key not available
        name: node.name
      });
    }
  };




  // Helper function to find node by ID in hierarchy
  const findNodeById = (nodeList: any[], targetId: number): any => {
    for (const node of nodeList) {
      if (node.nodeId === targetId) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Filter nodes based on search term
  const filterNodes = (nodeList: any[], searchTerm: string): any[] => {
    if (!searchTerm) return nodeList;
    
    return nodeList.reduce((filtered: any[], node) => {
      const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase());
      const filteredChildren = node.children ? filterNodes(node.children, searchTerm) : [];
      
      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren
        });
        
        // Auto-expand nodes that have matching children
        if (filteredChildren.length > 0 && !expandedItems.includes(node.nodeId.toString())) {
          setExpandedItems(prev => [...prev, node.nodeId.toString()]);
        }
      }
      
      return filtered;
    }, []);
  };

  // Render tree items recursively
  const renderTreeItems = (nodeList: any[]): React.ReactNode => {
    const filteredNodes = filterNodes(nodeList, searchTerm);
    
    return filteredNodes.map((node) => {
      const nodeIdStr = node.nodeId.toString();
      const hasChildren = node.children && node.children.length > 0;
      const isRootNode = node.nodeId === rootNodeId;
      
      return (
        <TreeItem
          key={nodeIdStr}
          itemId={nodeIdStr}
          label={
            <Box 
              sx={{ 
                py: 0.5,
                cursor: isRootNode ? 'default' : 'pointer',
                opacity: isRootNode ? 0.6 : 1,
                '&:hover': isRootNode ? {} : {
                  backgroundColor: 'action.hover',
                  borderRadius: 1
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isRootNode) {
                  handleNodeClick(nodeIdStr);
                }
              }}
            >
              <Typography variant="body2">
                {node.name}
                {isRootNode && ' (główny węzeł)'}
              </Typography>
              {node.description && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {node.description}
                </Typography>
              )}
            </Box>
          }
        >
          {hasChildren && renderTreeItems(node.children)}
        </TreeItem>
      );
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Ładowanie struktury...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Błąd ładowania struktury: {error}
      </Alert>
    );
  }





  // if (!nodes || nodes.length === 0) {
  //   return (
  //     <Alert severity="info" sx={{ mt: 2 }}>
  //       Brak dostępnych obiektów w strukturze.
  //     </Alert>
  //   );
  // }

   if (!hierarchy  || hierarchy .length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Brak dostępnych obiektów w strukturze.
      </Alert>
    );
  }







  return (
    <Box>
      {/* Search Box */}
      <TextField
        fullWidth
        placeholder="Szukaj obiektów..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
        size="small"
      />

      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kliknij na obiekt aby go wybrać. Główny węzeł nie może być wybrany.
      </Typography>

      {/* Tree View */}
      <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds as string[])}
          sx={{
            '& .MuiTreeItem-content': {
              padding: 0,
            },
            '& .MuiTreeItem-label': {
              padding: 0,
            }
          }}
        >
          {renderTreeItems(hierarchy)}
        </SimpleTreeView>
      </Box>
    </Box>
  );
};